// Copyright (c) 2021 WiseTime. All rights reserved.

import { useDispatch, useSelector } from "react-redux"
import { useCallback } from "react"
import { ListItemFavouriteType, ListItemType } from "features/shared/search-dropdown/types"
import * as TimelineActions from "features/timeline/actions"
import * as Actions from "features/activity-types/actions"
import * as Slice from "features/activity-types/slice"
import { ActivityType } from "generated/legebill/type/activity_type_pb"
import {
  FavouriteActivityType,
  CreateFavouriteActivityTypeRequest,
  DeleteFavouriteActivityTypeRequest,
  ReorderFavouriteActivityTypeRequest,
  SearchActivityTypesRequest,
  ListRecentActivityTypesRequest,
} from "generated/legebill/site/activitytype/v4/activity_type_pb"
import * as UserContextSlice from "features/user-context/slice"
import { Option } from "features/shared/Dropdown"
import { RECENT_LIST_ACTIVITY_TYPES_COUNT, SEARCH_LIST_ITEM_COUNT } from "features/shared/search-dropdown/config"
import {
  IdList,
  SetActivityTypeRequest
} from "generated/wisetime/user/timeline/v4/timeline_pb"
import { RecentActivityTypesOrderBy } from "features/activity-types/slice"
import { ActivityTypeRef } from "../../generated/wisetime/user/type/activity_type_ref_pb"

type UseActivityTypesDropdown = {
  onItemSelected?: (activityTypeRef: ActivityTypeRef) => void,
}
export const useActivityTypesDropdown = ({ onItemSelected }: UseActivityTypesDropdown) => {
  const dispatch = useDispatch()
  const isSearchingActivityTypes = useSelector(Slice.selectIsSearching)
  const favouriteActivityTypes = useSelector(Slice.selectFavouriteActivityTypes)
  const recentActivityTypes = useSelector(Slice.selectRecentActivityTypes)
  const activityTypesViewMode = useSelector(Slice.selectViewMode)
  const resultActivityTypes = useSelector(Slice.selectResultActivityTypes)
  const activityTypesSearchOrderBy = useSelector(Slice.selectSearchOrderBy)
  const activityTypesRecentOrderBy = useSelector(Slice.selectRecentOrderBy)
  const currentSite = useSelector(UserContextSlice.selectCurrentSite)
  const siteId = currentSite?.id
  const favouriteActivityTypesList = favouriteActivityTypes
    .map((favouriteActivityType: FavouriteActivityType.AsObject, siteListIndex): ListItemFavouriteType => {
      const { id, activityType } = favouriteActivityType
      return ({
        id,
        refId: activityType?.id || "",
        label: activityType?.label || "",
        description: activityType?.code || "",
        siteListIndex,
      })
    })
  const prepareList = useCallback((list: ActivityType.AsObject[]): ListItemType[] => list.map(({
    id,
    label,
    code,
  }: ActivityType.AsObject): ListItemType => ({
    id,
    label,
    description: code,
  })), [dispatch])
  const recentActivityTypesList = prepareList(recentActivityTypes)
  const resultActivityTypesList = prepareList(resultActivityTypes)
  const allActivityTypes: ListItemType[] = [
    ...favouriteActivityTypesList.map(({ refId, label, description }) => ({
      id: refId,
      label,
      description,
    })),
    ...recentActivityTypesList,
    ...resultActivityTypesList,
  ]

  const onAddActivityTypeToDestination = useCallback((id: string, timeGroupsIds: string[], timeRowsIds: string[]): void => {
    if (!currentSite || !siteId) {
      return
    }

    const setActivityTypeRequest = new SetActivityTypeRequest().setActivityTypeId(id)
    if (timeRowsIds.length > 0) {
      setActivityTypeRequest.setTimeRowIds(new IdList().setIdsList(timeRowsIds))
    }
    if (timeGroupsIds.length > 0) {
      setActivityTypeRequest.setTimeGroupIds(new IdList().setIdsList(timeGroupsIds))
    }
    const activityType = allActivityTypes.find(activityType => activityType.id === id)
    const activityTypeRef = new ActivityTypeRef().setId(id).setSiteId(siteId)
    if (activityType) {
      activityTypeRef.setLabel(activityType.label)
    }
    if (onItemSelected) {
      onItemSelected(activityTypeRef)
      return
    }
    dispatch(TimelineActions.setActivityType({ setActivityTypeRequest, activityType: activityTypeRef }))
  }, [dispatch, siteId, allActivityTypes])

  const onSearchActivityTypes = useCallback((searchText: string): void => {
    if (!siteId) {
      return
    }
    const request = new SearchActivityTypesRequest()
      .setSiteId(siteId)
      .setSearchText(searchText)
      .setOrderBy(activityTypesSearchOrderBy)
      .setPageSize(SEARCH_LIST_ITEM_COUNT)
    dispatch(Actions.searchActivityTypes(request))
  }, [dispatch, siteId, activityTypesSearchOrderBy])

  const onCreateFavouriteActivityType = useCallback((e, refId: string): void => {
    e.stopPropagation()
    const request = new CreateFavouriteActivityTypeRequest().setActivityTypeId(refId)
    dispatch(Actions.createFavouriteActivityType(request))
  }, [dispatch])

  const onDeleteFavouriteActivityType = useCallback((e, favouriteActivityTypeId: string): void => {
    e.stopPropagation()
    if (!siteId) {
      return
    }
    const request = new DeleteFavouriteActivityTypeRequest().setFavouriteActivityTypeId(favouriteActivityTypeId)
    dispatch(Actions.deleteFavouriteActivityType({
      request,
      siteId,
    }))
  }, [dispatch, siteId])

  const onResetSearchActivityTypes = useCallback((): void => {
    dispatch(Slice.resetResultActivityTypes())
  }, [dispatch])

  const onReorderFavouriteActivityType = useCallback((id: string, newIndex: number): void => {
    if (!siteId) {
      return
    }
    const request = new ReorderFavouriteActivityTypeRequest().setFavouriteActivityTypeId(id).setSiteListIndex(newIndex)
    dispatch(Actions.reorderFavouriteActivityType({
      request,
      siteId,
    }))
  }, [dispatch, siteId])
  const onChangeActivityTypesOrderBy = useCallback((value: Option["value"]): void => {
    dispatch(Slice.setSearchOrderBy(value as SearchActivityTypesRequest.OrderBy))
  }, [dispatch])
  const onChangeRecentActivityTypesOrderBy = useCallback((value: Option["value"]): void => {
    dispatch(Slice.setRecentOrderBy(value as RecentActivityTypesOrderBy))
  }, [dispatch])
  const onChangeActivityTypesViewMode = useCallback((value: Slice.ViewMode): void => {
    dispatch(Slice.setViewMode(value))
  }, [dispatch])
  const onFetchActivityTypes = useCallback((): void => {
    if (!siteId) {
      return
    }
    dispatch(Actions.fetchRecentActivityTypes({
      request: new ListRecentActivityTypesRequest()
        .setSiteId(siteId)
        .setPageSize(RECENT_LIST_ACTIVITY_TYPES_COUNT),
    }))
    dispatch(Actions.fetchFavouriteActivityTypes({ siteId }))
  }, [dispatch, siteId])

  return {
    isSearchingActivityTypes,
    favouriteActivityTypesList,
    recentActivityTypesList,
    resultActivityTypesList,
    activityTypesSearchOrderBy,
    activityTypesRecentOrderBy,
    activityTypesViewMode,
    onAddActivityTypeToDestination,
    onSearchActivityTypes,
    onResetSearchActivityTypes,
    onCreateFavouriteActivityType,
    onDeleteFavouriteActivityType,
    onReorderFavouriteActivityType,
    onChangeActivityTypesOrderBy,
    onChangeRecentActivityTypesOrderBy,
    onChangeActivityTypesViewMode,
    onFetchActivityTypes,
  }
}
